import {
	AspectMaintainer, Button, ButtonTypes, Card, Checkbox, type SelectOption, Select, SettingsLayout, Switch
} from "@clintonelec/react-storybook";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import "Components/ConfigLayout/Camera/DynamicZoomSettings/DynamicZoomSettings.less";
import HelpPopover from "Components/Global/HelpPopover";
import { selectDynamicZoom, setDynamicZoomAction } from "Data/Redux/Slices/Settings/Camera/DynamicZoom";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { produce } from "immer";
import { isEqual, times } from "lodash";
import { type ChangeEvent, type CSSProperties, type FormEvent, memo, useEffect, useRef, useState } from "react";
import { NotificationType, notify } from "src/Notifications";

const zoomLevelOptions: SelectOption[] = times(5, (index) => {
	const level = (index + 1) * 10;

	return {
		label: `${ level }%`,
		value: level
	};
});

const baseOptions: SelectOption[] = [ 1, 2, 3, 4, 5, 10, 15, 20, 25, 30 ].map(value => ({
	label: `${ value } Sec`,
	value: `${ value }`
}));

const durationSelectOptions: SelectOption[] = [
	...baseOptions,
	{
		label: "Transparent",
		value: "Transparent"
	}
];

const thresholdSelectOptions: SelectOption[] = [
	{
		label: "Immediately",
		value: "Immediately"
	},
	...baseOptions
];

const zoomLevelScaleMap: Record<number, number> = {
	10: 1.13,
	20: 1.25,
	30: 1.43,
	40: 1.68,
	50: 2.0
};

function DynamicZoomSettings() {
	const dispatch = useAppDispatch();
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const dynamicZoomSettings = useAppSelector(selectDynamicZoom);
	const [ localDynamicZoom, setLocalDynamicZoom ] = useState(dynamicZoomSettings);
	const { enabled, duration, threshold, zoomLevel, alarmIn, analytics, motion } = localDynamicZoom;
	const setDynamicZoom = () => dispatch(setDynamicZoomAction(localDynamicZoom));
	const [ previewZoom, setPreviewZoom ] = useState(false);
	const snapshotStyles: CSSProperties = {
		transform: previewZoom ? `scale(${ zoomLevelScaleMap[ zoomLevel ] })` : undefined
	};

	useEffect(() => () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	}, []);

	const handlePreviewZoomClick = () => {
		setPreviewZoom(!previewZoom);

		if (previewZoom && timeoutRef.current) {
			clearTimeout(timeoutRef.current);

			return;
		} else if (isNaN(Number(duration))) {
			return;
		}

		timeoutRef.current = setTimeout(() => {
			setPreviewZoom(false);
		}, Number(duration) * 1000);
	};

	const handleDynamicZoomEnabledChanged = (newEnabled: boolean) => {
		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.enabled = newEnabled;
		}));
	};

	const handleZoomLevelSelect = (newZoom: string | null) => {
		if (!newZoom) {
			return;
		}

		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.zoomLevel = Number(newZoom);
		}));
	};

	const handleDurationSelect = (newDuration: string | null) => {
		if (!newDuration) {
			return;
		}

		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.duration = newDuration;
		}));
	};

	const handleThresholdSelect = (newThreshold: string | null) => {
		if (!newThreshold) {
			return;
		}

		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.threshold = newThreshold;
		}));
	};

	const handleAnalyticsChecked = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.analytics = event.currentTarget.checked;
		}));
	};

	const handleMotionChecked = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.motion = event.currentTarget.checked;
		}));
	};

	const handleAlarmInChecked = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalDynamicZoom(produce(localDynamicZoom, draft => {
			draft.alarmIn = event.currentTarget.checked;
		}));
	};

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault();

		if (analytics || motion || alarmIn) {
			setDynamicZoom();
		} else {
			notify(NotificationType.ERROR, "You must select at least one Dynamic Zoom object.", null, undefined, {
				toastId: "dynamicZoomSource"
			});
		}
	};

	const handleFormReset = (event: FormEvent) => {
		event.preventDefault();

		setLocalDynamicZoom(dynamicZoomSettings);
	};

	const renderCameraSnapshot = () => {
		if (previewZoom || !enabled) {
			return;
		}

		return (
			<div
				className="zoom-preview"
				style={ { transform: `scale(${ 100 - zoomLevel }%)` } }
			/>
		);
	};

	return (
		<SettingsLayout.Form
			className="dynamic-zoom-settings"
			onSubmit={ handleFormSubmit }
			onReset={ handleFormReset }
		>
			<SettingsLayout.ScrollableContainer>
				<Card title="Dynamic Zoom" size="large">
					<div className="two-column-card-content">
						<div className="left-content">
							<div className="enabled-switch">
								<Switch
									checked={ enabled }
									onChange={ handleDynamicZoomEnabledChanged }
								/>
								<span>Enable Dynamic Zoom</span>
								<HelpPopover title="Dynamic Zoom">
									When enabled, the camera will digitally zoom-in the on-screen image to the area
									shown when triggered by a source such as Motion Detection. The image will return to
									normal after a set duration or when nothing is detected. This does not effect the
									network stream.
								</HelpPopover>
							</div>
							<div className="camera-container">
								<AspectMaintainer>
									<div className="camera-snapshot" style={ snapshotStyles }>
										{ renderCameraSnapshot() }
									</div>
								</AspectMaintainer>
							</div>
							<Button
								type={ ButtonTypes.TERTIARY }
								htmlType="button"
								disabled={ !enabled }
								className="preview-button"
								onClick={ handlePreviewZoomClick }
							>
								Preview Zoom
							</Button>
						</div>
						<div className="right-content">
							<div className="form-row">
								<span>Zoom Level</span>
								<Select
									allowClear={ false }
									className="form-select"
									disabled={ !enabled }
									onSelect={ handleZoomLevelSelect }
									options={ zoomLevelOptions }
									value={ zoomLevel }
								/>
							</div>
							<div className="form-row">
								<span>Dynamic Zoom Source</span>
								<div className="sources">
									<Checkbox
										checked={ analytics }
										onChange={ handleAnalyticsChecked }
										disabled={ !enabled }
									>
										<span>Analytics</span>
									</Checkbox>
									<Checkbox
										checked={ motion }
										onChange={ handleMotionChecked }
										disabled={ !enabled }
									>
										<span>Motion</span>
									</Checkbox>
									<Checkbox
										checked={ alarmIn }
										onChange={ handleAlarmInChecked }
										disabled={ !enabled }
									>
										<span>Alarm In</span>
									</Checkbox>
								</div>
							</div>
							<div className="form-row">
								<span>Duration</span>
								<Select
									allowClear={ false }
									className="form-select"
									disabled={ !enabled }
									onSelect={ handleDurationSelect }
									options={ durationSelectOptions }
									value={ duration }
								/>
							</div>
							<div className="form-row">
								<span>Threshold</span>
								<Select
									allowClear={ false }
									className="form-select"
									disabled={ !enabled }
									onSelect={ handleThresholdSelect }
									options={ thresholdSelectOptions }
									value={ threshold }
								/>
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(localDynamicZoom, dynamicZoomSettings) } />
		</SettingsLayout.Form>
	);
}

export default memo(DynamicZoomSettings);
